// The rules both sides run. The server is the authority; the client runs the same code to predict
// its own movement so a 150-250 ms mobile ping still feels instant (MMO-PLAN.md section 5).

export const TILE = 4;             // logical px per collision cell; 8 px = 1 m, so a cell is 0.5 m (iso rebuild 2026-10-14)
export const TICK_HZ = 20;         // overworld simulation rate
export const DT = 1 / TICK_HZ;
export const WALK_PX_S = 36;       // 4.5 m/s: a brisk game walk (real walking is ~11 px/s)
export const VIEW_R = 260;         // px (32 m): who you are told about (area of interest)

// collision.bin values
export const C_WALK = 0, C_BUILDING = 1, C_PROP = 2, C_CAR = 3, C_FURN = 4;   // C_FURN: a blocking prop INSIDE a building (cycle 011 #7)

// The body is a small box at the feet, so heads can overlap walls the way 16-bit RPGs read.
export const BODY_W = 5, BODY_H = 4;   // 0.6 x 0.5 m: fits a 1 m door
// against FURNITURE (C_FURN: a blocking prop inside a building) the body is narrower than a cell, so a 0.5 m gap between two
// tables is a way through (cycle 011 playability #7: a desktop tester walking through the Tavern's tables wedged in a one-cell
// notch between two high-tops, 21 px in 2 s). Walls, cars, void and the street's props (railings, patio sets, planters: the
// doorbot's back-in walk slipped between a patio's tables and came out on the wrong side of the door) keep the full body, so a
// gap between furniture and a wall stays shut
export const PROP_W = 3.2, PROP_H = 3.2;   // (0.4 px of play each side of a 4 px gap: the bots read a body 0.3 px off as in a wall, cellarbot)

export class Grid {
  /** is the point in an OPEN doorway? (set by IsoMap; door magnetism in stepOnce) */
  doorAt?: (px: number, py: number) => boolean;
  constructor(public w: number, public h: number, public cells: Uint8Array, public tile = TILE) {}
  blocked(px: number, py: number): boolean {
    const tx = Math.floor(px / this.tile), ty = Math.floor(py / this.tile);
    if (tx < 0 || ty < 0 || tx >= this.w || ty >= this.h) return true;
    return this.cells[ty * this.w + tx] !== C_WALK;
  }
  /** a cell that holds the full body back: anything but open ground and indoor furniture (out of the map counts) */
  hard(px: number, py: number): boolean {
    const tx = Math.floor(px / this.tile), ty = Math.floor(py / this.tile);
    if (tx < 0 || ty < 0 || tx >= this.w || ty >= this.h) return true;
    const c = this.cells[ty * this.w + tx]; return c !== C_WALK && c !== C_FURN;
  }
  prop(px: number, py: number): boolean {
    const tx = Math.floor(px / this.tile), ty = Math.floor(py / this.tile);
    return tx >= 0 && ty >= 0 && tx < this.w && ty < this.h && this.cells[ty * this.w + tx] === C_FURN;
  }
  boxBlocked(x: number, y: number): boolean {
    const l = x - BODY_W / 2, r = x + BODY_W / 2 - 0.01, t = y - BODY_H / 2, b = y + BODY_H / 2 - 0.01;
    // the body is wider (5 px) than a cell (4 px), so a one-cell wall can sit BETWEEN the left and right corners:
    // sample the middle column too (R6-002, red team 2026-10-15: dodges went through one-cell walls)
    if (this.hard(l, t) || this.hard(r, t) || this.hard(l, b) || this.hard(r, b) || this.hard(x, t) || this.hard(x, b)) return true;
    // furniture: the narrower body (PROP_W x PROP_H, under a cell, so its four corners are enough)
    const pl = x - PROP_W / 2, pr = x + PROP_W / 2 - 0.01, pt = y - PROP_H / 2, pb = y + PROP_H / 2 - 0.01;
    return this.prop(pl, pt) || this.prop(pr, pt) || this.prop(pl, pb) || this.prop(pr, pb);
  }
}

export interface Body { x: number; y: number }

// Move with axis-separated sliding. dirX/dirY in -1..1 (a stick can be analogue). A fast move (a dodge, a dash, a
// knock-back) is cut into steps of at most 1.5 px, so it can never jump over a wall thinner than itself.
export function step(g: Grid, b: Body, dirX: number, dirY: number, speed = WALK_PX_S, dt = DT): void {
  let dx = dirX, dy = dirY;
  const len = Math.hypot(dx, dy);
  if (len > 1) { dx /= len; dy /= len; }
  const dist = Math.hypot(dx, dy) * speed * dt;
  const n = Math.max(1, Math.ceil(dist / 1.5));
  for (let i = 0; i < n; i++) stepOnce(g, b, dx, dy, speed * dt / n);
}
function stepOnce(g: Grid, b: Body, dx: number, dy: number, d: number): void {
  const mx = dx * d, my = dy * d;
  // corner assist: blocked by an edge you only clip (a doorway, the end of a counter), slide sideways toward the gap
  // instead of stopping dead. REACH was 2.4 px; a 3-cell door leaves a 5 px body only +-3.5 px of play, and STEVE, walking
  // straight at the Tavern's door 4-7 px off its middle, stood against the wall (studio 008c, the director's G3 desktop
  // play). REACH is pushDoor's 8 px probe plus half a body and a nudge (10.5 = 15 nudges): any door your push OPENS is one the
  // slide can carry you into. At 8 px (dcaf5e2) PROPS on a phone opened the Tavern's door from 7.75 px short of its gap and
  // stood beside it, wedged, with CLOSE on his button (the director's d008cp2). test/doorbot.ts pushes at it from +-8 px.
  const NUDGE = 0.7, REACH = 10.5;
  // the side whose gap is NEAREST (not simply the first side that has one: a pocket 7 px east beat the door 5 px west), and
  // only if the first nudge that way is free: the sign to slide, or 0
  const nearGap = (gap: (o: number) => boolean, room: (o: number) => boolean): number => {
    let best = 0, bo = Infinity;
    for (const sd of [1, -1]) { if (!room(sd * NUDGE)) continue; for (let o = NUDGE; o <= REACH && o < bo; o += NUDGE) if (gap(sd * o)) { bo = o; best = sd; break; } }
    return best;
  };
  // DOOR MAGNETISM (cycle 009b, the director: "I don't accept a door that holds a player who walks at it on a diagonal"): the
  // corner assist below only slides a nearly straight push (cross component under 0.35), so a diagonal key at a doorway's
  // jamb slid along the wall past it (backin_check keys-at-the-door: 3 of 21 held on 8980400). When the way into the wall is
  // blocked here, the push has a real component into it (0.3 or more) and an OPEN doorway is within MAG px along the wall (or one cell past a counter flush with it),
  // you are carried toward the doorway this sub-step instead of along the wall (the push across it is set aside).
  const MAG = 20;   // px along the wall from your middle to the free spot in the doorway (2.5 m; 12 left a diagonal from 12 px off held)
  if (g.doorAt && mx !== 0 && my !== 0) {
    // a free spot o along the wall with an open doorway under the body's leading edge or one cell beyond it (a counter
    // flush with the wall beside a door holds you a cell short of the doorway: the Tavern's front, inside)
    const door1 = (x: number, y: number, ux: number, uy: number) => g.doorAt!(x, y) || g.doorAt!(x + ux * BODY_W / 2, y + uy * BODY_H / 2) || g.doorAt!(x + ux * (BODY_W / 2 + g.tile), y + uy * (BODY_H / 2 + g.tile));
    const toward = (o: number, nyy: number) => !g.boxBlocked(b.x + o, nyy) && door1(b.x + o, nyy, 0, Math.sign(my));
    const towardY = (o: number, nxx: number) => !g.boxBlocked(nxx, b.y + o) && door1(nxx, b.y + o, Math.sign(mx), 0);
    const ny = b.y + my, nx = b.x + mx;
    if (Math.abs(dy) >= 0.3 && g.boxBlocked(b.x, ny)) {
      let best = 0; for (let o = NUDGE; o <= MAG && !best; o += NUDGE) for (const sd of [Math.sign(mx), -Math.sign(mx)]) if (!best && toward(sd * o, ny)) best = sd * o;
      if (best) { const st = Math.sign(best) * Math.min(Math.abs(best), Math.abs(d)); if (!g.boxBlocked(b.x + st, b.y)) { b.x += st; return; } }   // (no faster than a walk: the step goes sideways, the next one in)
    } else if (Math.abs(dx) >= 0.3 && g.boxBlocked(nx, b.y)) {
      let best = 0; for (let o = NUDGE; o <= MAG && !best; o += NUDGE) for (const sd of [Math.sign(my), -Math.sign(my)]) if (!best && towardY(sd * o, nx)) best = sd * o;
      if (best) { const st = Math.sign(best) * Math.min(Math.abs(best), Math.abs(d)); if (!g.boxBlocked(b.x, b.y + st)) { b.y += st; return; } }
    }
  }
  const x0 = b.x, y0 = b.y;
  if (mx !== 0) {
    const nx = b.x + mx;
    if (!g.boxBlocked(nx, b.y)) b.x = nx;
    else if (Math.abs(dy) < 0.35) { const s = nearGap((o) => !g.boxBlocked(nx, b.y + o), (o) => !g.boxBlocked(b.x, b.y + o)); if (s) b.y += s * NUDGE; }
  }
  if (my !== 0) {
    const ny = b.y + my;
    if (!g.boxBlocked(b.x, ny)) b.y = ny;
    else if (Math.abs(dx) < 0.35) { const s = nearGap((o) => !g.boxBlocked(b.x + o, ny), (o) => !g.boxBlocked(b.x + o, b.y)); if (s) b.x += s * NUDGE; }
  }
  // a DIAGONAL push stopped dead on both axes (cycle 011 playability #7: into the notch between two tables, where the way
  // on is a 0.5 m gap a fraction of a pixel to one side): slide up to 2 px along either axis onto the line where the push
  // goes on, nearest first, no faster than the walk
  if (mx !== 0 && my !== 0 && b.x === x0 && b.y === y0) {
    const SL = 2.1, ST = 0.1;
    for (let o = ST; o <= SL; o += ST) {
      let s: [number, number] | null = null;
      for (const sd of [1, -1]) {
        if (!s && !g.boxBlocked(b.x + sd * o, b.y) && !g.boxBlocked(b.x + sd * o, b.y + my)) s = [sd, 0];
        if (!s && !g.boxBlocked(b.x, b.y + sd * o) && !g.boxBlocked(b.x + mx, b.y + sd * o)) s = [0, sd];
      }
      if (s) { const k = Math.min(o, Math.abs(d)); if (!g.boxBlocked(b.x + s[0] * k, b.y + s[1] * k)) { b.x += s[0] * k; b.y += s[1] * k; } break; }
    }
  }
}

// Facing from a direction: 0 down, 1 up, 2 left, 3 right (sheet row order).
/** 8 world directions from a world move: 0 E, 1 SE, 2 S, 3 SW, 4 W, 5 NW, 6 N, 7 NE (PROTO 9) */
export function facing(dx: number, dy: number, prev: number): number {
  if (dx === 0 && dy === 0) return prev;
  return ((Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) % 8) + 8) % 8;
}
/** facing() for WALKING (cycle 009b, spec 009 finding 7): keep the facing you have until the direction is more than 26 deg
 *  from its centre (22.5 + 3.5 of hysteresis), so a thumb resting near a boundary does not flip the figure (and its
 *  mirror) frame to frame. Client prediction and the server both use it for movement; an aim stays exact (facing).
 *  The band stays under 26.57 deg: a keyboard's screen diagonal (S+D) is a world move 18.4 deg from E and 26.57 from SE,
 *  and at 30 deg walking S then S+D kept the SE (straight down) figure walking down-right (hero_sheet_check, never
 *  facing 0 or 4). */
export const FACE_HOLD = (22.5 + 3.5) * Math.PI / 180;
export function facingHeld(dx: number, dy: number, prev: number): number {
  if (dx === 0 && dy === 0) return prev;
  const a = Math.atan2(dy, dx);
  const off = Math.abs(((a - (prev & 7) * (Math.PI / 4)) % (2 * Math.PI) + 3 * Math.PI) % (2 * Math.PI) - Math.PI);
  return off <= FACE_HOLD ? prev & 7 : facing(dx, dy, prev);
}
export const FACE8: [number, number][] = [[1, 0], [0.7071, 0.7071], [0, 1], [-0.7071, 0.7071], [-1, 0], [-0.7071, -0.7071], [0, -1], [0.7071, -0.7071]];

// ---- world clock (MMO-PLAN.md section 7): a 2-hour game day from the server's UTC clock, its
// day/night split stretched by Edmonton's real daylight for today's date, clamped so night is
// never under 15 or over 50 minutes.
export const GAME_DAY_S = 7200;
const EDM_LAT = 53.55;

export function daylightHours(date: Date): number {
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const doy = Math.floor((date.getTime() - start) / 86400000);
  const decl = 23.44 * Math.sin((2 * Math.PI / 365) * (doy - 81)) * Math.PI / 180;
  const lat = EDM_LAT * Math.PI / 180;
  const cosH = (Math.sin(-0.833 * Math.PI / 180) - Math.sin(lat) * Math.sin(decl)) / (Math.cos(lat) * Math.cos(decl));
  const H = Math.acos(Math.max(-1, Math.min(1, cosH)));
  return (2 * H * 180 / Math.PI) / 15;
}

export interface WorldTime {
  phase: number;      // 0..1 through the game day, 0 = midnight
  light: number;      // 0 (darkest allowed night) .. 1 (full day)
  isNight: boolean;
  dayLenS: number;    // real seconds of daylight in this game day
  clock: string;      // in-game HH:MM
}

export function worldTime(nowMs: number): WorldTime {
  const d = new Date(nowMs);
  const realDay = daylightHours(d) / 24;              // e.g. 0.70 in June, 0.31 in December
  let nightS = GAME_DAY_S * (1 - realDay);
  nightS = Math.max(15 * 60, Math.min(50 * 60, nightS));
  const dayS = GAME_DAY_S - nightS;
  const t = (nowMs / 1000) % GAME_DAY_S;               // seconds into this game day
  // layout: night centred on 0 (midnight). sunrise at nightS/2, sunset at nightS/2 + dayS
  const sunrise = nightS / 2, sunset = sunrise + dayS;
  const fade = 6 * 60;                                  // dusk/dawn ramps
  let light: number;
  if (t < sunrise - fade || t > sunset + fade) light = 0;
  else if (t < sunrise + fade) light = (t - (sunrise - fade)) / (2 * fade);
  else if (t > sunset - fade) light = 1 - (t - (sunset - fade)) / (2 * fade);
  else light = 1;
  // map the game day onto a 24 h clock with sunrise/sunset at Edmonton's real hours
  const realSunrise = 12 - daylightHours(d) / 2;
  let hours: number;
  if (t < sunrise) hours = (24 + (realSunrise - (24 - daylightHours(d)) * (sunrise - t) / nightS)) % 24;
  else if (t <= sunset) hours = realSunrise + daylightHours(d) * (t - sunrise) / dayS;
  else hours = (realSunrise + daylightHours(d) + (24 - daylightHours(d)) * (t - sunset) / nightS) % 24;
  const hh = Math.floor(hours), mm = Math.floor((hours - hh) * 60);
  return { phase: t / GAME_DAY_S, light, isNight: light < 0.5, dayLenS: dayS,
    clock: `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}` };
}

/** where this game day's sun is (the same layout worldTime uses): seconds into the day, sunrise, sunset, the ramp */
function sunTimes(nowMs: number): { t: number; sunrise: number; sunset: number; fade: number } {
  const realDay = daylightHours(new Date(nowMs)) / 24;
  const nightS = Math.max(15 * 60, Math.min(50 * 60, GAME_DAY_S * (1 - realDay)));
  const sunrise = nightS / 2;
  return { t: (nowMs / 1000) % GAME_DAY_S, sunrise, sunset: sunrise + GAME_DAY_S - nightS, fade: 6 * 60 };
}
/** The dusk-gated door (studio 008, 008-hearing.md 2.3): open from the sunset ramp (light falling below 0.8) until dawn
 *  (the morning ramp's start). The clock is the world's, the same for everyone. */
export function duskOpen(nowMs: number): boolean {
  const s = sunTimes(nowMs);
  return s.t >= s.sunset - 0.6 * s.fade || s.t < s.sunrise - s.fade;
}
/** ms from now until the next dawn (the morning ramp's start): when a party's dusk pass runs out */
export function untilDawnMs(nowMs: number): number {
  const s = sunTimes(nowMs);
  return (((s.sunrise - s.fade - s.t) % GAME_DAY_S + GAME_DAY_S) % GAME_DAY_S) * 1000;
}

// Real Edmonton season by date (STYLE.md: Animal Crossing-style northern dates, winter from Nov 26).
export function season(nowMs: number): 'fall' | 'winter' | 'spring' | 'summer' {
  const d = new Date(nowMs - 6 * 3600e3);              // Mountain-ish
  const md = (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
  if (md >= 1126 || md < 225) return 'winter';
  if (md < 601) return 'spring';
  if (md < 901) return 'summer';
  return 'fall';
}
